import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useCookies } from 'react-cookie';
import Modal from 'react-modal';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';
import { DefaultConsent } from '../../utils/constants';
import CloseIcon from '../../../../assets/img/icon/close.svg';
import ArrowIcon from '../../../../assets/img/icon/arrow.svg';
import Style from './style.module.scss';

const SettingsModal = ({ isModalVisible, setIsModalVisible, setIsActive }) => {
  const { t } = useTranslation();
  const [adsCheck, setAdsCheck] = useState(false);
  const [cookies, setCookie] = useCookies();

  useEffect(() => {
    if (cookies.cc_cookie && cookies.cc_cookie.level.indexOf('ads') !== -1) {
      setAdsCheck(true);
    }
  }, [cookies]);

  const handleAgreeCookie = async () => {
    setCookie(
      'cc_cookie',
      JSON.stringify({
        level: ['necessary', 'ads'],
        revision: 0,
        data: null,
        rfc_cookie: false,
      }),
      {
        path: '/',
        domain: `.${window.location.hostname}`,
        secure: true,
        sameSite: 'Lax',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        encode: (value) => value,
      }
    );

    const consentArguments = {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      reklam: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    };

    if (window.gtag) {
      window.gtag('consent', 'update', consentArguments);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent_update',
      consent_status: 'granted',
    });

    setTimeout(() => {
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
        });
      }
    }, 100);

    setIsModalVisible(false);
    setIsActive(false);
  };

  const handleSaveCookie = async () => {
    setCookie(
      'cc_cookie',
      JSON.stringify({
        level: adsCheck ? ['necessary', 'ads'] : ['necessary'],
        revision: 0,
        data: null,
        rfc_cookie: false,
      }),
      {
        path: '/',
        domain: `.${window.location.hostname}`,
        secure: true,
        sameSite: 'Lax',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        encode: (value) => value,
      }
    );

    const consentArguments = adsCheck
      ? {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          reklam: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        }
      : {
          ...DefaultConsent,
        };

    if (window.gtag) {
      window.gtag('consent', 'update', consentArguments);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookieConsentUpdated',
      consent_status: adsCheck ? 'granted' : 'denied',
    });

    if (adsCheck) {
      setTimeout(() => {
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_location: window.location.href,
            page_path: window.location.pathname,
          });
        }
      }, 100);
    }

    setIsModalVisible(false);
    setIsActive(false);
  };

  const handleClose = async () => {
    setIsModalVisible(false);
  };

  return (
    <Modal
      className={Style.settingsModal}
      overlayClassName={Style.overlay}
      isOpen={isModalVisible}
      onRequestClose={handleClose}
      ariaHideApp={false}
    >
      <div className={Style.popupHeader}>
        <h4>{t('cookiePopup.cookieOptions')}</h4>
        <button className={Style.closeHeader} onClick={handleClose}>
          <img src={CloseIcon} alt="close" />
        </button>
      </div>
      <div className={Style.popupContent}>
        <div className={Style.description}>{t('cookiePopup.popupDescription')}</div>
        <Accordion className={Style.accordion} allowMultipleExpanded allowZeroExpanded>
          <AccordionItem className={Style.accordionItem}>
            <AccordionItemHeading>
              <AccordionItemButton>
                <div className={Style.accordionTitle}>
                  <AccordionItemState>
                    {(state) => {
                      return (
                        <img
                          src={ArrowIcon}
                          alt="arrow"
                          className={clsx(Style.arrow, { [Style.collapsed]: state.expanded })}
                        />
                      );
                    }}
                  </AccordionItemState>
                  <p className={Style.title}>{t('cookiePopup.requiredCookies')}</p>
                  <label className={Style.checkboxLabel} htmlFor="necessaryInput">
                    <input
                      name="necessaryInput"
                      className={Style.checkboxInput}
                      type="checkbox"
                      value="necessary"
                      checked
                      disabled
                    />
                    <span className={Style.switch}>
                      <span className={Style.on} />
                      <span className={Style.off} />
                    </span>
                  </label>
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className={Style.accordionPanel}>
              {t('cookiePopup.requiredCookiesDesc')}
            </AccordionItemPanel>
          </AccordionItem>
          <AccordionItem className={Style.accordionItem}>
            <AccordionItemHeading>
              <AccordionItemButton className={clsx({ [Style.notChecked]: !adsCheck })}>
                <div className={Style.accordionTitle}>
                  <AccordionItemState>
                    {(state) => {
                      return (
                        <img
                          src={ArrowIcon}
                          alt="arrow"
                          className={clsx(Style.arrow, { [Style.collapsed]: state.expanded })}
                        />
                      );
                    }}
                  </AccordionItemState>
                  <p className={Style.title}>{t('cookiePopup.adsCookies')}</p>
                  <label className={Style.checkboxLabel} htmlFor="adsInput">
                    <input
                      name="adsInput"
                      className={Style.checkboxInput}
                      type="checkbox"
                      value="ads"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => setAdsCheck(!adsCheck)}
                      defaultChecked={adsCheck}
                    />
                    <span className={Style.switch}>
                      <span className={Style.on} />
                      <span className={Style.off} />
                    </span>
                  </label>
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className={Style.accordionPanel}>
              {t('cookiePopup.adsCookiesDesc')}
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      </div>
      <div className={Style.popupFooter}>
        <button onClick={handleSaveCookie}>{t('cookiePopup.save')}</button>
        <button onClick={handleAgreeCookie}>{t('cookiePopup.approve')}</button>
      </div>
    </Modal>
  );
};

export default SettingsModal;
